import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useTransform, useAnimationFrame } from "framer-motion";
import { BOTANICALS, PACK_IMAGE } from "@/data/botanicals";
import IngredientDialog from "@/components/site/IngredientDialog";

const EASE = [0.16, 1, 0.3, 1];

const OrbitCard = ({ herb, index, total, angle, radius, onSelect }) => {
  const theta = (index / total) * Math.PI * 2;
  const x = useTransform(angle, (a) => Math.sin(a + theta) * radius);
  const depth = useTransform(angle, (a) => Math.cos(a + theta));
  const scale = useTransform(depth, (d) => 0.55 + ((d + 1) / 2) * 0.5);
  const opacity = useTransform(depth, (d) => 0.3 + ((d + 1) / 2) * 0.7);
  const y = useTransform(depth, (d) => (1 - d) * -26);
  const zIndex = useTransform(depth, (d) => Math.round((d + 1) * 50));

  return (
    <motion.button
      style={{ x, y, scale, opacity, zIndex }}
      onClick={() => onSelect(herb)}
      data-testid={`orbit-herb-${herb.id}`}
      className="group absolute left-1/2 top-1/2 -ml-14 -mt-20 sm:-ml-16 sm:-mt-24 w-28 sm:w-32 focus:outline-none"
      aria-label={`Learn about ${herb.name}`}
    >
      <div className="relative rounded-xl overflow-hidden border border-[#C5A880]/25 shadow-xl shadow-black/40 transition-transform duration-300 group-hover:-translate-y-2">
        <img src={herb.image} alt={herb.name} className="w-28 h-36 sm:w-32 sm:h-40 object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#041a0f]/85 via-transparent to-transparent" />
        <span className="absolute bottom-2 left-0 right-0 text-center font-display italic text-sm text-[#F5F1E8]">
          {herb.name}
        </span>
      </div>
    </motion.button>
  );
};

const BotanicalCarousel = () => {
  const angle = useMotionValue(0);
  const [selected, setSelected] = useState(null);
  const [radius, setRadius] = useState(420);
  const hovering = useRef(false);

  useEffect(() => {
    const update = () => setRadius(Math.min(440, Math.max(150, window.innerWidth * 0.36)));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useAnimationFrame((t, delta) => {
    if (!hovering.current && !selected) angle.set(angle.get() + delta * 0.00016);
  });

  return (
    <section id="botanicals" data-testid="botanical-carousel-section" className="relative bg-[#041a0f] py-28 lg:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="max-w-2xl"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#C5A880]">The Botanical Orbit</span>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl text-[#F5F1E8] tracking-tight leading-tight">
            Ten sacred herbs, <span className="italic text-[#C5A880]">one perfect blend.</span>
          </h2>
          <p className="mt-5 text-[#F5F1E8]/60 text-base leading-relaxed font-light">
            Touch any botanical to reveal its Ayurvedic intelligence — its benefit, its dosha, and its role in the smoking ritual.
          </p>
        </motion.div>
      </div>

      <div
        className="orbit-stage relative mt-10 h-[560px] sm:h-[620px]"
        onMouseEnter={() => (hovering.current = true)}
        onMouseLeave={() => (hovering.current = false)}
        data-testid="botanical-orbit-stage"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: EASE }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[55]"
        >
          <div className="absolute -inset-10 rounded-full bg-[#C5A880]/12 blur-3xl" />
          <motion.img
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            src={PACK_IMAGE}
            alt="Sage & Spirit pack"
            data-testid="carousel-pack-image"
            className="relative w-40 sm:w-52 rounded-lg shadow-2xl shadow-black/60 border border-[#C5A880]/20"
          />
        </motion.div>

        {BOTANICALS.map((herb, i) => (
          <OrbitCard
            key={herb.id}
            herb={herb}
            index={i}
            total={BOTANICALS.length}
            angle={angle}
            radius={radius}
            onSelect={setSelected}
          />
        ))}
      </div>

      <p className="text-center text-xs uppercase tracking-[0.25em] text-[#F5F1E8]/35 mt-4">
        Click a botanical · hover to pause the orbit
      </p>

      <IngredientDialog herb={selected} onClose={() => setSelected(null)} />
    </section>
  );
};

export default BotanicalCarousel;
